import { Injectable, Inject } from '@angular/core';

import { Store } from '@ngrx/store';

import { WindowDoorPartialState } from './window-door.reducer';
import { LoadWindowDoor } from './window-door.actions';
import { SharedFacade } from '@icc/configurator/shared';
import { filter, map, withLatestFrom } from 'rxjs/operators';
import { ConfigurationsService } from '@icc/configurator/shared';
import { AppConfigFactory, APP_CONFIG, ConfiguratorsDataService, BaseIssuesService } from '@icc/shared';
import { isNotNullOrUndefined } from '@icc/shared/helpers';
import { FittingService } from '../modules/handles/fitting.service';
import { DoorActiveConfiguration } from '@icc/window-door/types';
import { WindowActiveConfiguration } from '@icc/window-door/types';
import { BrowserProfilesService } from '../profiles/profiles.service';
import { DoorPortalsService } from '../door-portals/door-portals.service';
import { HardwareService } from '../modules/door/hardware.service';
import { LocksService } from '../modules/door/locks.service';
import { HandlesService } from '../modules/handles/handles.service';

// Helper function for door type checking
function isDoor(conf: any): conf is DoorActiveConfiguration {
    return DoorActiveConfiguration.is(conf);
}

@Injectable()
export class WindowDoorFacade {
    constructor(
        private store: Store<WindowDoorPartialState>,
        private sharedFacade: SharedFacade,
        private fittingService: FittingService,
        private dataService: ConfiguratorsDataService,
        private configurationsService: ConfigurationsService<'window' | 'door'>,
        private profilesService: BrowserProfilesService,
        private issuesService: BaseIssuesService,
        @Inject(APP_CONFIG) private config: AppConfigFactory,
        private doorPortalsService: DoorPortalsService,
        private hardwareService: HardwareService,
        private locksService: LocksService,
        private handlesService: HandlesService
    ) {}

    shape$ = this.configurationsService.configuration$.pipe(
        map(conf => conf.Shape),
        filter(shape => Boolean(shape))
    );
    configuration$ = this.configurationsService.configuration$;
    doorConfiguration$ = this.configurationsService.configuration$.pipe(
        filter(isDoor)
    );

    sashTypes$ = this.dataService.data$.pipe(map(data => data.sashTypes));
    fillings$ = this.dataService.data$.pipe(
        map(data => data.fillings),
        filter(isNotNullOrUndefined)
    );

    layouts$ = this.dataService.layouts$.pipe(filter(isNotNullOrUndefined));

    colors$ = this.dataService.data$.pipe(
        map(data => [...(data.windowColorsAll || [])]),
        filter(isNotNullOrUndefined)
    );

    colorGroups$ = this.dataService.data$.pipe(
        map(data => data.windowColorGroups),
        filter(isNotNullOrUndefined)
    );

    windowSystems$ = this.dataService.data$.pipe(
        map(data => data.windowLines),
        filter(isNotNullOrUndefined)
    );

    selectedFilling$ = this.configurationsService.configuration$.pipe(
        filter(conf => conf.Sashes.length > 0),
        map(conf => conf.Sashes[0].glazing)
    );

    selectedModel$ = this.configurationsService.configuration$.pipe(
        map(conf =>
            conf.Sashes.length > 0
                ? conf.Sashes[0].glazing
                : DoorActiveConfiguration.is(conf)
                ? conf.Model
                : null
        )
    );

    selectedModelForSashActive$ = this.configurationsService.configuration$.pipe(
        map(conf => {
            if (!DoorActiveConfiguration.is(conf)) {
                return null;
            }
            let layoutId: number | null = null;
            let layoutName: string | null = null;
            if(conf.ModelOptions?.predefinedLayoutId){
                layoutId = conf.ModelOptions?.predefinedLayoutId;
                layoutName = conf.ModelOptions?.predefinedLayoutName;
            }
            
            if (layoutId) {
                return {
                    id: 'layout_' + layoutId,
                    name: layoutName || '',
                };
            }
            return conf.Model;
        })
    );

    modelHasBeenChosen$ = this.configurationsService.configuration$.pipe(
        map(conf =>
            DoorActiveConfiguration.is(conf) && conf.ModelOptions.hasBeenChosen
        )
    );

    selectedGlazingSpacer$ = this.configurationsService.configuration$.pipe(
        map(conf => conf.WarmEdge)
    );

    selectedGlazingBead$ = this.configurationsService.configuration$.pipe(
        filter(conf => conf.Sashes.length > 0),
        map(conf => conf.UsedProfiles.find(p => p.id === conf.Sashes[0].glazingBead.profileId)),
        filter(isNotNullOrUndefined)
    );

    selectedGlazingBeadInSashes$ = this.configurationsService.configuration$.pipe(
        filter(conf => conf.Sashes.length > 0),
        map(conf => {
            const sash = conf.Sashes.find(s => s.type && s.type.type !== 'F');
            return sash && conf.UsedProfiles.find(p => p.id === sash.glazingBead.profileId);
        }),
        filter(isNotNullOrUndefined)
    );

    selectedGlazingBeadInFixed$ = this.configurationsService.configuration$.pipe(
        filter(conf => conf.Sashes.length > 0),
        map(conf => {
            const sash = conf.Sashes.find(s => s.type &&s.type.type === 'F');
            return sash && conf.UsedProfiles.find(p => p.id === sash.glazingBead.profileId);
        }),
        filter(isNotNullOrUndefined)
    );

    selectedMuntinColorInner$ = this.configurationsService.configuration$.pipe(
        map(conf => conf.MuntinsData.color)
    );

    selectedMuntinColorOuter$ = this.configurationsService.configuration$.pipe(
        map(conf => conf.MuntinsData.colorOut)
    );

    selectedMuntinType$ = this.configurationsService.configuration$.pipe(
        map(conf => conf.MuntinsData.type)
    );

    selectedGlazing$ = this.configurationsService.configuration$.pipe(
        map(conf => conf.Glazing.type)
    );

    selectedMuntinSizeId$ = this.configurationsService.configuration$.pipe(
        map(conf => conf.MuntinsData.sizeId)
    );
    selectedDuplex$ = this.configurationsService.configuration$.pipe(
        map(conf => conf.MuntinsData.duplex)
    );

    selectedSealColor$ = this.configurationsService.configuration$.pipe(
        map(conf => conf.SealColor)
    );

    selectedSiliconeColor$ = this.configurationsService.configuration$.pipe(
        map(conf => conf.SiliconeColor)
    );

    selectedHandle$ = this.configurationsService.configuration$.pipe(
        filter(conf => conf.Sashes.length > 0),
        map(conf => conf.Sashes[0].handle),
        filter(isNotNullOrUndefined)
    );

    selectedHandleColor$ = this.configurationsService.configuration$.pipe(
        filter(conf => conf.Sashes.length > 0),
        map(conf => conf.Sashes[0].handleColor),
        filter(isNotNullOrUndefined)
    );

    selectedFitting$ = this.configurationsService.configuration$.pipe(
        map(conf => conf.Fitting),
        filter(isNotNullOrUndefined)
    );

    selectedColor$ = this.configurationsService.configuration$.pipe(
        map(conf => {
            if (conf.Colors && conf.Colors.frame) {
                if (conf.Colors.frame.outer && conf.Colors.frame.outer.id) {
                    return conf.Colors.frame.outer;
                } else {
                    return conf.Colors.frame.core;
                }
            }
        })
    );

    outerFrameColor$ = this.configurationsService.configuration$.pipe(
        map(conf => {
            if (conf.Colors && conf.Colors.frame) {
                if (conf.Colors.frame.outer && conf.Colors.frame.outer.id) {
                    return conf.Colors.frame.outer;
                } else {
                    return conf.Colors.frame.core;
                }
            }
        })
    );

    innerFrameColor$ = this.configurationsService.configuration$.pipe(
        map(conf => {
            if (conf.Colors && conf.Colors.frame) {
                if (conf.Colors.frame.inner && conf.Colors.frame.inner.id) {
                    return conf.Colors.frame.inner;
                } else {
                    return conf.Colors.frame.core;
                }
            }
        })
    );

    coreFrameColor$ = this.configurationsService.configuration$.pipe(
        map(conf => conf.Colors && conf.Colors.frame && conf.Colors.frame.core)
    );

    alushellFrameColor$ = this.configurationsService.configuration$.pipe(
        map(conf => conf.Colors && conf.Colors.frame && conf.Colors.frame.alushell)
    );

    outerSashColor$ = this.configurationsService.configuration$.pipe(
        map(conf => {
            if (conf.Colors && conf.Colors.sash) {
                if (conf.Colors.sash.outer && conf.Colors.sash.outer.id) {
                    return conf.Colors.sash.outer;
                } else {
                    return conf.Colors.sash.core;
                }
            }
        })
    );

    innerSashColor$ = this.configurationsService.configuration$.pipe(
        map(conf => {
            if (conf.Colors && conf.Colors.sash) {
                if (conf.Colors.sash.inner && conf.Colors.sash.inner.id) {
                    return conf.Colors.sash.inner;
                } else {
                    return conf.Colors.sash.core;
                }
            }
        })
    );

    coreSashColor$ = this.configurationsService.configuration$.pipe(
        map(conf => conf.Colors && conf.Colors.sash && conf.Colors.sash.core)
    );

    alushellSashColor$ = this.configurationsService.configuration$.pipe(
        map(conf => conf.Colors && conf.Colors.sash && conf.Colors.sash.alushell)
    );

    hasAlushell$ = this.configurationsService.configuration$.pipe(map(conf => conf.HasAlushell));

    colorSashExt$ = this.configurationsService.configuration$.pipe(map(conf => conf.ColorsSashExt));

    height$ = this.configurationsService.configuration$.pipe(map(conf => conf.Height));

    width$ = this.configurationsService.configuration$.pipe(map(conf => conf.Width));

    accessories$ = this.configurationsService.configuration$.pipe(map(conf => conf.Accessories));
    sideAccessories$ = this.configurationsService.configuration$.pipe(
        map(conf => conf.SideAccessories)
    );

    sashes$ = this.configurationsService.configuration$.pipe(map(conf => conf.Sashes));

    selectedHinge$ = this.configurationsService.configuration$.pipe(map(conf => conf.Hinge));

    hingeColor$ = this.configurationsService.configuration$.pipe(map(conf => conf.HingeColor));

    locks$ = this.configurationsService.configuration$.pipe(map(conf => conf.Lock));

    innerPullBoxSelect$ = this.configurationsService.configuration$.pipe(
        map(conf => this.fittingService.innerPullBox(conf))
    );

    innerLeverBoxSelect$ = this.configurationsService.configuration$.pipe(
        map(conf => this.fittingService.innerLeverBox(conf))
    );

    outerLeverBoxSelect$ = this.configurationsService.configuration$.pipe(
        map(conf => this.fittingService.outerLeverBox(conf))
    );

    outerPullBoxSelect$ = this.configurationsService.configuration$.pipe(
        map(conf => this.fittingService.outerPullBox(conf))
    );

    windowLeverBoxSelect$ = this.configurationsService.configuration$.pipe(
        map(conf => this.fittingService.windowLeverBox(conf))
    );

    hingeBoxSelect$ = this.configurationsService.configuration$.pipe(
        map(conf => this.fittingService.hingeBoxSelect(conf))
    );

    showInnerPullBox$ = this.configurationsService.configuration$.pipe(
        map(conf => this.fittingService.showInnerPullBox(conf))
    );

    showDoubleLeverBox$ = this.configurationsService.configuration$.pipe(
        map(
            conf => this.fittingService.showInnerLeverBox(conf) && conf.HandleType === 'DoubleLever'
        )
    );

    showInnerLeverBox$ = this.configurationsService.configuration$.pipe(
        map(
            conf => this.fittingService.showInnerLeverBox(conf) && conf.HandleType !== 'DoubleLever'
        )
    );

    showOuterPullBox$ = this.configurationsService.configuration$.pipe(
        map(conf => this.fittingService.showOuterPullBox(conf))
    );

    showWindowLeverBox$ = this.configurationsService.configuration$.pipe(
        map(conf => this.fittingService.showWindowLeverBox(conf))
    );

    handleType$ = this.configurationsService.configuration$.pipe(map(conf => conf.HandleType));

    woodType$ = this.configurationsService.configuration$.pipe(map(conf => conf.Wood));

    balcony$ = this.configurationsService.configuration$.pipe(map(conf => conf.Balcony ));

    layout$ = this.configurationsService.configuration$.pipe(map(conf => conf.Layout));

    lowThreshold$ = this.configurationsService.configuration$.pipe(
        filter(WindowActiveConfiguration.is),
        map(conf => conf.Frames.some(frame => frame.lowThreshold))
    );

    weldFinishType$ = this.configurationsService.configuration$.pipe(
        map(conf => conf.weldFinishType)
    );

    drainagePosition$ = this.configurationsService.configuration$.pipe(
        map(conf => conf.drainagePosition)
    );

    alushellType$ = this.configurationsService.configuration$.pipe(map(conf => conf.AlushellType));

    monoblock$ = this.configurationsService.configuration$.pipe(map(conf => conf.Monoblock));

    system$ = this.configurationsService.configuration$.pipe(
        withLatestFrom(this.dataService.data$),
        map(([conf, data]) => {
            if (conf.System && conf.System.subsystem && data.windowLinesAll) {
                return data.windowLinesAll.find(s => !s.subsystem && Number(conf.System.parent_id) === Number(s.id));
            }
            return conf.System;
        })
    );

    profileSet$ = this.configurationsService.configuration$.pipe(map(conf => conf.ProfileSet));

    oneGlazing$ = this.configurationsService.configuration$.pipe(map(conf => conf.OneGlazing));
    oneGlazingBead$ = this.configurationsService.configuration$.pipe(
        map(
            conf =>
                conf.OneGlazing
                && this.config().IccConfig.Configurators.glazingBeadsSelect
                && (conf.OneGlazingBead
                    || !this.config().IccConfig.Configurators.glazingBeadsSelectSash)
        )
    );
    oneGlazingBeadSashes$ = this.configurationsService.configuration$.pipe(
        map(
            conf =>
                conf.OneGlazing
                && this.config().IccConfig.Configurators.glazingBeadsSelect
                && this.config().IccConfig.Configurators.glazingBeadsSelectSash
                && !conf.OneGlazingBead
                && conf.OneGlazingBeadSash.sashes
        )
    );
    oneGlazingBeadFix$ = this.configurationsService.configuration$.pipe(
        map(
            conf =>
                conf.OneGlazing
                && this.config().IccConfig.Configurators.glazingBeadsSelect
                && this.config().IccConfig.Configurators.glazingBeadsSelectSash
                && !conf.OneGlazingBead
                && conf.OneGlazingBeadSash.fix
        )
    );

    hasWarmEdge$ = this.configurationsService.configuration$.pipe(map(conf => conf.HasWarmEdge));

    hasThresholdColorSelect$ = this.configurationsService.configuration$.pipe(
        map(conf => {
            const profile = this.profilesService.getProfile(this.profilesService.getUsedThresholdId(conf));
            return profile && profile.priceLevelId !== null;
        })
    );

    selectedLock$ = this.configurationsService.configuration$.pipe(map(conf => conf.Lock && Number(conf.Lock.id)));

    showOuterHandles$ = this.configurationsService.configuration$.pipe(
        map(conf => {
            const handle = conf.Handle;
            return handle && handle.outer_handles_id?.length > 0 && conf.HandleType === 'DoubleLever';
        })
    );

    hasRoller$ = this.configurationsService.configuration$.pipe(
        map(conf => {
            return conf.hasRoller;
        })
    );

    selectHandlerType$ = this.configurationsService.configuration$.pipe(
        map(conf => {
            const selectHandlerType = this.config().IccConfig.Configurators.sliding_door.outerHandles
                && ['sliding_door', 'window'].indexOf(conf.type) > -1;
            const height = conf.Height;
            const activeSashesOnBottom = conf.Sashes.filter(sash => sash?.type?.type !== 'FF' && sash?.type?.type !== 'F').some(sash => Number(sash.rHeight + sash.ry) === Number(height));
            return selectHandlerType && activeSashesOnBottom;

        }
    ));

    mosquito$ = this.configurationsService.configuration$.pipe(map(conf => conf.mosquito));

    isBlockedAddToOffer$ = this.configurationsService.configuration$.pipe(
        map(conf =>
            this.issuesService.isBlockedAddToOffer(conf)
        )
    );

    // Door-specific observables
    doorSizes$ = this.doorConfiguration$.pipe(map(conf => conf.doorSizes));

    selectedHardwareType$ = this.doorConfiguration$.pipe(
        filter(isDoor),
        map(
            conf =>
                conf.doorHardware
                && this.hardwareService.getTypes(conf).find(t => t.id == conf.doorHardware.type)
        )
    );

    hardwareColor$ = this.doorConfiguration$.pipe(
        filter(isDoor),
        map(conf => conf.HingeColor)
    );

    hardwareTypeBoxSelect$ = this.doorConfiguration$.pipe(
        map(conf => this.hardwareService.hardwareTypeBoxSelect(conf))
    );

    showHardwareTypeBox$ = this.doorConfiguration$.pipe(
        map(conf => this.hardwareService.hardwareTypes.length > 0 && conf.doorHardware.set)
    );

    hardwareSet$ = this.doorConfiguration$.pipe(
        map(conf => conf.doorHardware.set)
    );

    hardwareSets$ = this.doorConfiguration$.pipe(
        map(conf => this.hardwareService.getAvailableSets(conf))
    );

    pull$ = this.doorConfiguration$.pipe(
        map(conf => conf.doorHardware.pull),
        map(id => this.hardwareService.pulls.find(t => Number(t.id) === id))
    );

    electronicLock$ = this.doorConfiguration$.pipe(
        map(conf => conf.doorHardware.electronicLock),
        map(id => this.hardwareService.electronicLocks.find(t => id && Number(t.id) === id.id))
    );

    plateType$ = this.doorConfiguration$.pipe(
        map(conf => conf.doorHardware.plateType),
        map(id => this.hardwareService.platesTypes.find(t => Number(t.id) === id))
    );

    doorViewer$ = this.doorConfiguration$.pipe(
        map(conf => conf.doorHardware.doorViewer),
        map(id => this.hardwareService.doorViewers.find(t => t.id == id))
    );

    doorViewerPunching$ = this.doorConfiguration$.pipe(
        map(conf => conf.doorHardware.doorViewerPunching)
    );

    plateMainLock$ = this.doorConfiguration$.pipe(
        map(conf => conf.doorHardware.plates.mainLock),
        map(id => this.hardwareService.platesToMainLock.find(t => Number(t.id) === id))
    );

    plateAdditionalLock$ = this.doorConfiguration$.pipe(
        map(conf => conf.doorHardware.plates.additionalLock),
        map(id => this.hardwareService.platesToAdditionalLock.find(t => Number(t.id) === id))
    );

    plateMainLockBoxSelect$ = this.doorConfiguration$.pipe(
        map(conf => this.hardwareService.plateMainLockBoxSelect(conf))
    );

    plateAdditionalLockBoxSelect$ = this.doorConfiguration$.pipe(
        map(conf => this.hardwareService.plateAdditionalLockBoxSelect(conf))
    );

    outerPlateAdditionalLockBoxSelect$ = this.doorConfiguration$.pipe(
        map(conf => this.hardwareService.outerPlateAdditionalLockBoxSelect(conf))
    );

    innerPlateMainLock$ = this.doorConfiguration$.pipe(
        map(conf => conf.doorHardware.plates.mainLock),
        map(id => this.hardwareService.innerPlatesToMainLock.find(t => Number(t.id) === id))
    );

    innerPlateAdditionalLock$ = this.doorConfiguration$.pipe(
        map(conf => conf.doorHardware.plates.additionalLock),
        map(id => this.hardwareService.innerPlatesToAdditionalLock.find(t => Number(t.id) === id))
    );

    innerPlateAdditionalLockBoxSelect$ = this.doorConfiguration$.pipe(
        map(conf => this.hardwareService.plateAdditionalLockBoxSelect(conf))
    );

    outerPlateMainLock$ = this.doorConfiguration$.pipe(
        map(conf => conf.doorHardware.outerPlates.mainLock),
        map(id => this.hardwareService.outerPlatesToMainLock.find(t => Number(t.id) === Number(id)))
    );

    outerPlateAdditionalLock$ = this.doorConfiguration$.pipe(
        map(conf => conf.doorHardware.outerPlates.additionalLock),
        map(id => this.hardwareService.outerPlatesToAdditionalLock.find(t => Number(t.id) === id))
    );

    outerPlateMainLockBoxSelect$ = this.doorConfiguration$.pipe(
        map(conf => this.hardwareService.outerPlateMainLockBoxSelect(conf))
    );

    cylinderMainLock$ = this.doorConfiguration$.pipe(
        map(conf => conf.doorHardware.cylinders.mainLock),
        map(id => this.hardwareService.cylindersToMainLock.find(t => Number(t.id) === id))
    );

    cylinderMainLockBoxSelect$ = this.doorConfiguration$.pipe(
        map(conf => this.hardwareService.cylinderMainLockBoxSelect(conf))
    );

    cylinderAdditionalLock$ = this.doorConfiguration$.pipe(
        map(conf => conf.doorHardware.cylinders.additionalLock),
        map(id => this.hardwareService.cylindersToAdditionalLock.find(t => Number(t.id) === id))
    );

    cylinderAdditionalLockBoxSelect$ = this.doorConfiguration$.pipe(
        map(conf => this.hardwareService.cylinderAdditionalLockBoxSelect(conf))
    );

    cylinderSystem$ = this.doorConfiguration$.pipe(
        map(conf => conf.doorHardware.cylinders.system),
        map(id => this.hardwareService.cylinderSystems.find(t => Number(t.id) === id))
    );

    doorHandleAdjustment$ = this.doorConfiguration$.pipe(
        map(conf => conf.doorHardware.doorHandleAdjustment),
        map(id => this.hardwareService.doorHandleAdjustments.find(t => t.id == id))
    );

    showDoorHandleAdjustmentBox$ = this.doorConfiguration$.pipe(
        map(conf => this.hardwareService.doorHandleAdjustments.length > 0
            && conf.doorHardware.set === 'leverPull')
    );

    platesBoxSelect$ = this.doorConfiguration$.pipe(
        map(conf => this.hardwareService.platesBoxSelect(conf))
    );

    outerPlatesBoxSelect$ = this.doorConfiguration$.pipe(
        map(conf => this.hardwareService.outerPlatesBoxSelect(conf))
    );

    pullsBoxSelect$ = this.doorConfiguration$.pipe(
        map(conf => this.hardwareService.pullsBoxSelect(conf))
    )

    recessedPullsBoxSelect$ = this.doorConfiguration$.pipe(
        map(conf => this.hardwareService.recessedPullsBoxSelect(conf))
    )

    cylindersBoxSelect$ = this.doorConfiguration$.pipe(
        map(conf => this.hardwareService.cylindersBoxSelect(conf))
    );

    electronicLockBoxSelect$ = this.doorConfiguration$.pipe(
        map(conf => this.hardwareService.electronicLockBoxSelect(conf))
    )

    doorViewerBoxSelect$ = this.doorConfiguration$.pipe(
        map(conf => this.hardwareService.doorViewerBoxSelect(conf))
    )

    locksBoxSelect$ = this.doorConfiguration$.pipe(
        map(conf => this.hardwareService.locksBoxSelect(conf))
    )

    lock$ = this.doorConfiguration$.pipe(
        map(conf => conf.Lock.id),
        map(id => this.locksService.locks.find(l => Number(l.id) === Number(id)))
    )

    hinge$ = this.doorConfiguration$.pipe(
        map(conf => this.handlesService.findHingesBySystem(conf.System)
            .find((hinge) => Number(hinge.id) === Number(conf.Hinge.id))
        )
    );

    doorHingeBoxSelect$ = this.doorConfiguration$.pipe(
        map(conf => this.hardwareService.hingeBoxSelect(conf))
    );

    decorativeHingeCovers$ = this.doorConfiguration$.pipe(
        map(conf => conf.doorHardware.decorativeHingeCovers),
        map(id => this.hardwareService.decoHingeCovers.find(t => t.id == id))
    );

    decorativeHingeCoversColor$ = this.doorConfiguration$.pipe(
        map(conf => conf.doorHardware.decorativeHingeCoversColor),
        map(id => this.hardwareService.hardwareColors.find(t => Number(t.id) == Number(id)))
    );

    showDecorativeHingeCovers$ = this.doorConfiguration$.pipe(
        map(conf => conf.System.decorative_hinge_covers)
    );

    selectedFrame$ = this.doorConfiguration$.pipe(
        map(conf => {
            const position = 1;
            const frame = conf.Frames[0];
            const profileId = frame.frame[position].profileId;
            return this.profilesService.getProfile(profileId);
        })
    );

    selectedThreshold$ = this.doorConfiguration$.pipe(
        map(conf => {
            return this.profilesService.getProfile(this.profilesService.getUsedThresholdId(conf));
        })
    );

    showDoorViewer$ = this.doorConfiguration$.pipe(
        map(conf => this.hardwareService.getAvailableDoorViewers(conf).filter(viewers => viewers.id !== 0).length > 0)
    );

    showDoorViewerPunchingOption$ = this.doorConfiguration$.pipe(
        map(conf => conf.System.available_door_viewer_punching)
    )

    seperateFrameAndSashShortening$ = this.doorConfiguration$.pipe(
        map(conf => conf.System.seperate_frame_and_sash_shortening)
    )

    getContextInfoInDoorViewer$ = this.doorConfiguration$.pipe(
        map(conf => this.hardwareService.getContextInfo(conf, 'viewer'))
    );

    fillingWithIntegratePull$ = this.doorConfiguration$.pipe(
        map(conf => this.hardwareService.getSelectedFilling(conf)?.integrated_pull || false)
    );

    fillingWithRecessedPull$ = this.doorConfiguration$.pipe(
        map(conf => this.hardwareService.getRecessedPulls(conf).length > 0 || false)
    );

    loadAll() {
        this.store.dispatch(new LoadWindowDoor());
    }
}

// Legacy export
export {WindowDoorFacade as WindowFacade};

// Legacy export for DoorFacade
export {WindowDoorFacade as DoorFacade};
